import { Inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { ApiUrl } from './constants';
import { RequestParams } from '@src/app/shared/types/common';
import { mediaLimits } from '@src/app/constants/listing-limits';
import { RoutesEnum } from '@src/app/enums';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private isLoadingSubject = new BehaviorSubject<any>({});
  isLoading$: Observable<any> = this.isLoadingSubject.asObservable();

  private newsCategoriesSubject = new BehaviorSubject<Record<string, any>[]>(
    [],
  );
  newsCategories$: Observable<Record<string, any>[]> =
    this.newsCategoriesSubject.asObservable();

  private blogsCategoriesSubject = new BehaviorSubject<Record<string, any>[]>(
    [],
  );
  blogsCategories$: Observable<Record<string, any>[]> =
    this.blogsCategoriesSubject.asObservable();

  private newsListingLimitSubject = new BehaviorSubject<number>(
    mediaLimits.news,
  );
  newsListingLimit$: Observable<number> =
    this.newsListingLimitSubject.asObservable();

  private blogListingLimitSubject = new BehaviorSubject<number>(
    mediaLimits.blog,
  );
  blogListingLimit$: Observable<number> =
    this.blogListingLimitSubject.asObservable();

  private newsListingPageSubject = new BehaviorSubject<number>(1);
  newsListingPage$: Observable<number> =
    this.newsListingPageSubject.asObservable();

  private blogListingPageSubject = new BehaviorSubject<number>(1);
  blogListingPage$: Observable<number> =
    this.blogListingPageSubject.asObservable();

  constructor(
    @Inject(Router) private router: Router,
    private apiService: ApiService,
  ) {}

  get newsListingLimit(): number {
    return this.newsListingLimitSubject.value;
  }

  get blogListingLimit(): number {
    return this.blogListingLimitSubject.value;
  }

  get newsListingPage(): number {
    return this.newsListingPageSubject.value;
  }

  get blogListingPage(): number {
    return this.blogListingPageSubject.value;
  }

  get newsCategories(): Record<string, any>[] {
    return this.newsCategoriesSubject.value;
  }

  get blogsCategories(): Record<string, any>[] {
    return this.blogsCategoriesSubject.value;
  }

  setIsLoading(value: boolean): void {
    this.isLoadingSubject.next(value);
  }

  setNewsListingLimit(limit: number) {
    this.newsListingLimitSubject.next(limit);
  }

  setBlogListingLimit(limit: number) {
    this.blogListingLimitSubject.next(limit);
  }

  setNewsListingPage(page: number) {
    this.newsListingPageSubject.next(page);
  }

  setBlogListingPage(page: number) {
    this.blogListingPageSubject.next(page);
  }

  setNewsCategories(list: Record<string, any>[]): void {
    this.newsCategoriesSubject.next(list);
  }

  setBlogsCategories(list: Record<string, any>[]): void {
    this.blogsCategoriesSubject.next(list);
  }

  /**
   * @description Handling API call errors
   * @param {HttpErrorResponse} error
   */
  handleError(error: HttpErrorResponse): void {
    this.isLoadingSubject.next(false);

    if (
      error.error?.statusCode === 404 ||
      error.error?.status === 404 ||
      error?.status === 404
    ) {
      this.router.navigate(['/' + RoutesEnum.NOT_FOUND], {
        skipLocationChange: true,
      });
    } else if (error.error?.message) {
      console.error(error.error?.message);
    }
  }

  getNewsAll(
    params: RequestParams,
    options: { withLoading?: boolean } = { withLoading: true },
  ): Observable<Record<string, any>[]> {
    if (options.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getNewsAll,
      params,
    });
  }

  getNewsCategories(params: RequestParams): Observable<Record<string, any>[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getNewsCategories,
      params,
    });
  }

  getNewsBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getNewsItem(slug),
      params,
    });
  }

  getBlogsAll(
    params: RequestParams,
    options: { withLoading?: boolean } = { withLoading: true },
  ): Observable<Record<string, any>[]> {
    if (options.withLoading) {
      this.isLoadingSubject.next(true);
    }

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBlogsAll,
      params,
    });
  }

  getBlogsCategories(params: RequestParams): Observable<Record<string, any>[]> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBlogsCategories,
      params,
    });
  }

  getBlogBySlug(
    slug: number | string,
    params: RequestParams,
  ): Observable<Record<string, any>> {
    this.isLoadingSubject.next(true);

    return this.apiService.request({
      method: 'get',
      endpoint: ApiUrl.getBlogsItem(slug),
      params,
    });
  }

  postNewsSubsciption(
    body: Record<string, any>,
    params?: any,
  ): Observable<Record<string, any>> {
    return this.apiService.request({
      method: 'post',
      endpoint: ApiUrl.postNewsSubsciption,
      params,
      body,
    });
  }
}
